import { useEffect, useState } from 'react';
import { auth, getErrorMessage } from '../../utils/api';
import { Loader2, AlertCircle } from 'lucide-react';

/**
 * Magic-link landing for newly-invited users.
 *
 * URL shape: /retailer/welcome?email=<email>&token=<temp_password>
 *
 * Submits the credentials via a plain axios call (NOT a <form> submission),
 * so Chrome's "Save password?" prompt is never triggered for the disposable
 * temporary password. On success, we hand off to the parent RetailerApp via
 * the `onLogin` callback, which sees `must_reset_password=true` and routes
 * straight to the forced-reset screen.
 *
 * If the link is reused after the user already changed their password, the
 * temp pw is no longer valid → 401 → we show a friendly error with a link
 * back to the manual login page.
 */
export default function RetailerWelcomeLink({ onLogin }) {
  const [status, setStatus] = useState('loading'); // loading | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Scrub the URL bar IMMEDIATELY (synchronously, before any network call)
    // so Chrome's password manager never sees credential-like content.
    const hash = (window.location.hash || '').replace(/^#/, '');
    const search = (window.location.search || '').replace(/^\?/, '');
    try {
      window.history.replaceState({}, document.title, '/retailer/welcome');
    } catch (_) { /* non-critical */ }

    const params = new URLSearchParams(hash || search);
    // New format: just a UUID at #wt=<uuid>. No email, no password. ever.
    const wt = params.get('wt') || '';
    // Legacy format kept for emails that were sent before the UUID migration.
    const email = params.get('email') || '';
    const token = params.get('token') || '';

    if (!wt && (!email || !token)) {
      setErrorMsg('Invalid welcome link. Please return to your email and try again, or sign in manually.');
      setStatus('error');
      return;
    }

    (async () => {
      try {
        const res = wt
          ? await auth.welcomeExchange({ wt })
          : await auth.welcomeLogin({ email, token });
        const { access_token, user } = res.data;
        if (!access_token || !user) {
          throw new Error('Unexpected response');
        }
        onLogin(access_token, user);
      } catch (err) {
        setErrorMsg(getErrorMessage(err, 'This welcome link has expired. Please sign in with the password you set.'));
        setStatus('error');
      }
    })();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center" data-testid="welcome-link-loading">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          <p className="text-sm text-gray-600 mt-4">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div
        className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center"
        data-testid="welcome-link-error"
      >
        <AlertCircle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
        <h2 className="text-xl font-semibold mb-2">Welcome link couldn't be used</h2>
        <p className="text-sm text-gray-600 mb-6">{errorMsg}</p>
        <a
          href="/retailer"
          className="inline-block px-5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm"
        >
          Go to sign in
        </a>
      </div>
    </div>
  );
}
